<template>
  <div class="about-page">

    <!-- ── 1. CINEMATIC HERO ──────────────────────────────── -->
    <main class="about-hero" ref="heroSection">
      <div class="about-hero-bg">
        <div class="hero-glow"></div>
      </div>
      <div class="about-hero-inner">
        <h1 class="about-title">
          <span class="title-line"><span class="title-text">{{ $t('about.title') }}</span></span>
        </h1>
        <p class="about-lead hero-el">{{ $t('about.description') }}</p>
      </div>
    </main>

    <!-- ── 2. STAGGERED BENTO GRID (Mission/Vision/Values) ─ -->
    <section class="cards-section" ref="cardsSection">
      <div class="section-inner">
        <div class="section-header cards-header">
          <span class="section-label">{{ $t('about.whatDrivesUs') }}</span>
        </div>
        
        <div class="bento-grid">
          <div 
            class="bento-card" 
            v-for="(card, i) in cards" :key="i"
            ref="bentoCards"
          >
            <div class="bento-glow"></div>
            <div class="bento-content">
              <div class="bento-num">0{{ i + 1 }}</div>
              <h3 class="bento-title">{{ $t(card.titleKey) }}</h3>
              <p class="bento-desc">{{ $t(card.descKey) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── 3. STICKY SCROLL SECTION (Built for the trade) ── -->
    <section class="built-section" ref="builtSection">
      <div class="section-inner built-layout">
        <div class="built-text-col">
          <div class="built-sticky">
            <span class="section-label">{{ $t('about.built.label') }}</span>
            <h2 class="section-title built-title">{{ $t('about.built.title') }}</h2>
            <p class="built-desc">{{ $t('about.built.desc1') }}</p>
          </div>
        </div>
        
        <div class="built-stats-col">
          <div 
            class="stat-card" 
            v-for="stat in builtStats" :key="stat.label"
            ref="statCards"
          >
            <div class="stat-card-inner">
              <div class="stat-val">{{ stat.val }}</div>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── 4. SCROLL-DRAWN TIMELINE ──────────────────────── -->
    <section class="timeline-section" ref="timelineSection">
      <div class="section-inner">
        <div class="section-header tl-header">
          <span class="section-label">{{ $t('about.timeline.label') }}</span>
          <h2 class="section-title">{{ $t('about.timeline.title') }}</h2>
        </div>
        
        <div class="timeline-container">
          <div class="tl-track"></div>
          <div class="tl-fill" ref="tlFill"></div>
          
          <div class="timeline-items">
            <div 
              class="timeline-item" 
              v-for="(item, i) in timeline" :key="i"
              ref="tlItems"
            >
              <div class="tl-dot"></div>
              <div class="tl-content">
                <div class="tl-year">{{ $t(item.yearKey) }}</div>
                <div class="tl-event">{{ $t(item.eventKey) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── 5. FLOATING CTA ───────────────────────────────── -->
    <section class="cta-section" ref="ctaSection">
      <div class="cta-inner" ref="ctaInner">
        <div class="cta-bg-glow"></div>
        <h2 class="cta-title">{{ $t('about.cta.title') }}</h2>
        <p class="cta-sub">{{ $t('about.cta.description') }}</p>
        <div class="cta-actions">
          <router-link to="/forbussines">
            <button class="btn-primary btn-large">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 3v12M6 3C6 3 9 5 12 5s6-2 6-2v12c0 0-3 2-6 2s-6-2-6-2M18 15v6M6 15v6"/>
              </svg>
              {{ $t('navigation.joinAsBarber') }}
            </button>
          </router-link>
          <router-link to="/contact">
            <button class="btn-ghost btn-large">{{ $t('about.cta.getInTouch') }}</button>
          </router-link>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'AboutPage',
  data() {
    return {
      cards: [
        { titleKey: 'about.mission.title', descKey: 'about.mission.description' },
        { titleKey: 'about.vision.title',  descKey: 'about.vision.description' },
        { titleKey: 'about.values.title',  descKey: 'about.values.description' },
      ],
      timeline: [
        { yearKey: 'about.timeline.items.0.year', eventKey: 'about.timeline.items.0.event' },
        { yearKey: 'about.timeline.items.1.year', eventKey: 'about.timeline.items.1.event' },
        { yearKey: 'about.timeline.items.2.year', eventKey: 'about.timeline.items.2.event' },
      ],
      gsapCtx: null,
    }
  },
  computed: {
    builtStats() {
      return [
        { val: '5+',   label: this.$t('about.built.stats.countries') },
        { val: '20min',label: this.$t('about.built.stats.time') },
        { val: '€0',   label: this.$t('about.built.stats.cost') },
      ]
    }
  },
  mounted() {
    setTimeout(() => {
      this.$nextTick(() => {
        this.initGSAP()
      })
    }, 100)
  },
  beforeUnmount() {
    if (this.gsapCtx) {
      try { this.gsapCtx.revert() } catch (_) {}
      this.gsapCtx = null
    }
    try {
      ScrollTrigger.getAll().forEach(t => { try { t.kill() } catch (_) {} })
      ScrollTrigger.clearScrollMemory()
    } catch (_) {}
  },
  methods: {
    initGSAP() {
      this.gsapCtx = gsap.context(() => {
        
        // 1. Hero Reveal (Restored bouncier skew animation)
        const heroTl = gsap.timeline();
        heroTl.fromTo('.title-text', 
          { y: '120%', skewY: 5 }, 
          { y: '0%', skewY: 0, duration: 1, ease: 'power4.out' }
        ).fromTo('.hero-el',
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out' },
          "-=0.6"
        );

        // 2. Bento Grid Cards (Restored scale bounce)
        if (this.$refs.cardsSection && this.$refs.bentoCards) {
          gsap.fromTo(this.$refs.bentoCards,
            { opacity: 0, y: 40, scale: 0.95 },
            { opacity: 1, y: 0, scale: 1, duration: 0.8, stagger: 0.15, ease: 'back.out(1.2)', 
              scrollTrigger: { trigger: this.$refs.cardsSection, start: 'top 80%' } }
          );
        }

        // 3. Sticky Section Controls (Restored heavier slide)
        if (this.$refs.builtSection && this.$refs.statCards) {
          gsap.fromTo('.built-title', 
            { opacity: 0, x: -30 }, 
            { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out', scrollTrigger: { trigger: this.$refs.builtSection, start: 'top 75%' } }
          );
          gsap.fromTo(this.$refs.statCards,
            { opacity: 0, x: 50 },
            { opacity: 1, x: 0, duration: 0.8, stagger: 0.2, ease: 'power3.out', 
              scrollTrigger: { trigger: this.$refs.builtSection, start: 'top 65%' } }
          );
        }

        // 4. Timeline Filling Logic
        if (this.$refs.timelineSection && this.$refs.tlFill && this.$refs.tlItems) {
          gsap.fromTo(this.$refs.tlFill,
            { scaleY: 0 },
            { scaleY: 1, ease: 'none', transformOrigin: 'top center',
              scrollTrigger: { trigger: '.timeline-container', start: 'top 60%', end: 'bottom 60%', scrub: true } }
          );
          
          this.$refs.tlItems.forEach((item) => {
            gsap.fromTo(item,
              { opacity: 0, x: 30 },
              { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out',
                scrollTrigger: { trigger: item, start: 'top 65%' } }
            );
          });
        }

        // 5. CTA Block Float (Restored pop animation)
        if (this.$refs.ctaInner) {
          gsap.fromTo(this.$refs.ctaInner,
            { opacity: 0, y: 50, scale: 0.98 },
            { opacity: 1, y: 0, scale: 1, duration: 1, ease: 'power3.out',
              scrollTrigger: { trigger: this.$refs.ctaSection, start: 'top 85%' } }
          );
        }

        setTimeout(() => ScrollTrigger.refresh(), 200);
      });
    }
  }
}
</script>

<style scoped>
/* ── DESIGN TOKENS ─────────────────────────────────────── */
.about-page {
  --accent:        #4a7fa5;
  --accent-light:  #6499bb;
  --accent-glow:   rgba(74, 127, 165, 0.25);
  --surface:       rgba(15, 20, 32, 0.5);
  --surface-hover: rgba(25, 32, 48, 0.7);
  --border:        rgba(255, 255, 255, 0.08);
  --text-primary:  #f0f4f8;
  --text-secondary:#94a3b8;
  --text-muted:    #64748b;
  color: var(--text-primary);
  overflow-x: hidden;
}

/* ── SHARED UTILITIES ──────────────────────────────────── */
.section-inner { max-width: 1140px; margin: 0 auto; padding: 0 1.5rem; }
.section-label { 
  display: inline-block; font-size: 0.65rem; font-weight: 800; 
  letter-spacing: 0.25em; text-transform: uppercase; color: var(--accent-light); 
  margin-bottom: 1rem;
}
.section-title { 
  font-size: clamp(2rem, 4vw, 3rem); font-weight: 900; 
  letter-spacing: -0.03em; color: var(--text-primary); line-height: 1.1; 
}

/* ── 1. HERO ───────────────────────────────────────────── */
.about-hero {
  position: relative; min-height: 65vh; display: flex; align-items: center; 
  justify-content: center; text-align: center; padding: 10rem 1.5rem 4rem;
}
.about-hero-bg {
  position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background: radial-gradient(ellipse at top center, rgba(74,127,165,0.08) 0%, transparent 70%);
}
.about-hero-inner { position: relative; z-index: 1; max-width: 800px; display: flex; flex-direction: column; align-items: center; }
.title-line { display: block; overflow: hidden; padding-bottom: 0.2rem; }
.title-text { display: inline-block; will-change: transform; }
.about-title {
  font-size: clamp(3rem, 7vw, 5rem); font-weight: 900; line-height: 1.05; 
  letter-spacing: -0.04em; margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #fff 30%, #8ca6c2 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
}
.about-lead { font-size: clamp(1.05rem, 2vw, 1.25rem); color: var(--text-secondary); line-height: 1.7; max-width: 620px; }

/* ── 2. BENTO GRID ─────────────────────────────────────── */
.cards-section { padding: 6rem 0; }
.cards-header { text-align: center; margin-bottom: 4rem; }
.bento-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
.bento-card {
  position: relative; background: var(--surface); border: 1px solid var(--border);
  border-radius: 24px; padding: 3rem 2rem; overflow: hidden;
  transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), border-color 0.4s;
  backdrop-filter: blur(20px);
}
.bento-card:hover { transform: translateY(-6px); border-color: var(--accent-light); }
.bento-glow {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background: radial-gradient(circle at 50% 0%, rgba(74, 127, 165, 0.12), transparent 60%);
  opacity: 0; transition: opacity 0.5s; pointer-events: none;
}
.bento-card:hover .bento-glow { opacity: 1; }
.bento-content { position: relative; z-index: 2; }
.bento-num {
  font-size: 3.5rem; font-weight: 900; color: transparent;
  -webkit-text-stroke: 1px rgba(255,255,255,0.12); line-height: 1; margin-bottom: 1.5rem;
  transition: -webkit-text-stroke 0.4s;
}
.bento-card:hover .bento-num { -webkit-text-stroke: 1px var(--accent-light); }
.bento-title { font-size: 1.25rem; font-weight: 800; margin-bottom: 0.75rem; color: #fff; }
.bento-desc { font-size: 0.92rem; color: var(--text-secondary); line-height: 1.65; }

/* ── 3. STICKY SCROLL SECTION ──────────────────────────── */
.built-section { padding: 8rem 0; border-top: 1px solid var(--border); background: rgba(0,0,0,0.15); }
.built-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 6rem; align-items: start; }
.built-sticky { position: sticky; top: 8rem; }
.built-title { margin-bottom: 1.5rem; }
.built-desc { font-size: 1.05rem; color: var(--text-secondary); line-height: 1.75; }
.built-stats-col { display: flex; flex-direction: column; gap: 1.5rem; }
.stat-card {
  background: var(--surface); border: 1px solid var(--border); border-radius: 20px;
  padding: 2.5rem; backdrop-filter: blur(12px);
  transition: background 0.3s, border-color 0.3s;
}
.stat-card:hover { background: var(--surface-hover); border-color: rgba(255,255,255,0.18); }
.stat-val { font-size: clamp(3rem, 5vw, 4rem); font-weight: 900; color: var(--accent-light); line-height: 1; margin-bottom: 0.4rem; letter-spacing: -0.04em; }
.stat-label { font-size: 0.82rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: var(--text-muted); }

/* ── 4. TIMELINE ───────────────────────────────────────── */
.timeline-section { padding: 8rem 0; border-top: 1px solid var(--border); }
.tl-header { text-align: center; margin-bottom: 5rem; }
.timeline-container { max-width: 600px; margin: 0 auto; position: relative; padding: 1rem 0; }
.tl-track { position: absolute; top: 0; bottom: 0; left: 19px; width: 2px; background: var(--border); border-radius: 2px; }
.tl-fill {
  position: absolute; top: 0; bottom: 0; left: 19px; width: 2px;
  background: linear-gradient(to bottom, var(--accent-light), var(--accent));
  border-radius: 2px; transform-origin: top center; transform: scaleY(0);
}
.timeline-item { position: relative; padding-left: 4rem; margin-bottom: 4rem; }
.timeline-item:last-child { margin-bottom: 0; }
.tl-dot {
  position: absolute; left: 15px; top: 4px; width: 10px; height: 10px;
  border-radius: 50%; background: var(--surface); border: 2px solid var(--accent-light);
  box-shadow: 0 0 10px var(--accent-glow); z-index: 2;
}
.tl-year { font-size: 0.85rem; font-weight: 800; letter-spacing: 0.1em; color: var(--accent-light); margin-bottom: 0.5rem; }
.tl-event { font-size: 1.05rem; color: var(--text-secondary); line-height: 1.6; }

/* ── 5. FLOATING CTA ───────────────────────────────────── */
.cta-section { padding: 8rem 1.5rem; border-top: 1px solid var(--border); position: relative; }
.cta-inner {
  max-width: 900px; margin: 0 auto; text-align: center; position: relative;
  background: var(--surface); border: 1px solid var(--border); border-radius: 32px;
  padding: 5rem 3rem; backdrop-filter: blur(24px); box-shadow: 0 40px 80px rgba(0,0,0,0.5);
  overflow: hidden;
}
.cta-bg-glow {
  position: absolute; top: -50%; left: 50%; transform: translateX(-50%);
  width: 600px; height: 600px; border-radius: 50%;
  background: radial-gradient(circle, rgba(74, 127, 165, 0.12) 0%, transparent 60%);
  pointer-events: none;
}
.cta-title { font-size: clamp(2.2rem, 5vw, 3.2rem); font-weight: 900; letter-spacing: -0.03em; color: #fff; margin-bottom: 1rem; }
.cta-sub { font-size: 1.05rem; color: var(--text-secondary); max-width: 480px; margin: 0 auto 2.5rem; line-height: 1.65; }
.cta-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 1rem; position: relative; z-index: 2; }

/* ── BUTTONS ───────────────────────────────────────────── */
.btn-primary {
  display: inline-flex; align-items: center; gap: 8px; background: var(--accent); color: #fff;
  font-weight: 700; font-size: 0.95rem; padding: 1rem 2rem; border-radius: 12px; border: none;
  cursor: pointer; text-decoration: none; transition: all 0.25s;
}
.btn-primary:hover { background: var(--accent-light); transform: translateY(-3px); box-shadow: 0 10px 25px var(--accent-glow); }
.btn-ghost {
  display: inline-flex; align-items: center; justify-content: center; background: transparent; 
  color: var(--text-primary); font-weight: 600; font-size: 0.95rem; padding: 1rem 2rem; 
  border-radius: 12px; border: 1px solid var(--border); cursor: pointer; text-decoration: none; transition: all 0.25s;
}
.btn-ghost:hover { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.2); transform: translateY(-3px); }

/* ── RESPONSIVE ────────────────────────────────────────── */
@media (max-width: 960px) {
  .bento-grid { grid-template-columns: 1fr; }
  .built-layout { grid-template-columns: 1fr; gap: 4rem; }
  .built-sticky { position: relative; top: 0; text-align: center; }
  .built-stats-col { padding-top: 0; }
}
@media (max-width: 640px) {
  .cta-inner { padding: 4rem 1.5rem; }
  .timeline-container { padding-left: 1rem; }
}
</style>